import { useEffect, useCallback, useRef } from 'react'
import { useEscapeKey } from '../hooks/useEscapeKey'

/**
 * Reusable Modal component with:
 * - Escape key to close
 * - Click outside (overlay) to close
 * - Consistent styling
 * - Focus management
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal should close
 * @param {string} title - Modal title (optional)
 * @param {ReactNode} children - Modal content
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * @param {boolean} showCloseButton - Whether to show the X button (default: true)
 * @param {object} style - Additional styles for the modal content
 * @param {boolean} closeOnOverlayClick - Whether a backdrop click closes the modal
 *        (default: true). Set to false for data-entry forms so a stray click on the
 *        backdrop doesn't discard unsaved input (Escape/× still close).
 * @param {object} contentStyle - Additional styles for the content wrapper. Use to
 *        zero out the default padding when the modal body manages its own layout
 *        (e.g. a pinned header / scrollable body / pinned footer).
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  style = {},
  dataTestId,
  closeOnOverlayClick = true,
  contentStyle = {}
}) => {
  const modalRef = useRef(null)

  // Handle escape key
  useEscapeKey(isOpen, onClose)

  // Handle click outside
  const handleOverlayClick = useCallback((e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose()
    }
  }, [onClose, closeOnOverlayClick])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [isOpen])

  // Focus the modal when it opens
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isOpen])

  if (!isOpen) return null

  const sizeStyles = {
    sm: { maxWidth: '400px' },
    md: { maxWidth: '600px' },
    lg: { maxWidth: '800px' },
    xl: { maxWidth: '1000px' },
    full: { maxWidth: '95%', width: '95%' }
  }

  return (
    <div
      style={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        data-testid={dataTestId}
        style={{
          ...styles.modal,
          ...sizeStyles[size],
          ...style
        }}
      >
        {(title || showCloseButton) && (
          <div style={styles.header}>
            {title && <h2 id="modal-title" style={styles.title}>{title}</h2>}
            {showCloseButton && (
              <button
                onClick={onClose}
                style={styles.closeButton}
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div style={{ ...styles.content, ...contentStyle }}>
          {children}
        </div>
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    outline: 'none'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #dee2e6'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.75rem',
    cursor: 'pointer',
    color: '#666',
    padding: '0',
    lineHeight: 1,
    marginLeft: 'auto'
  },
  content: {
    padding: '1.5rem',
    overflowY: 'auto',
    flex: 1
  }
}

export default Modal
